import React, { useMemo } from "react";
import { Box, Typography, Chip } from "@mui/material";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import styles from "./areaChart.module.scss";

interface AreaConfig {
  dataKey: string;
  color: string;
  label: string;
}

interface AreaChartProps {
  title?: string;
  data?: any[];
  xAxisKey?: string;
  areas?: AreaConfig[];
  height?: number;
  showThisYear?: boolean;
}

const CustomAreaChart: React.FC<AreaChartProps> = ({
  title = "Area Chart",
  data = [],
  xAxisKey = "year",
  areas = [
    { dataKey: "total", color: "#5b6b9e", label: "Total" },
    { dataKey: "inProcess", color: "#00d2aa", label: "In Process" },
  ],
  height = 280,
  showThisYear = false,
}) => {
  // Calculate dynamic domain and ticks based on data
  const { maxValue, ticks } = useMemo(() => {
    if (!data || data.length === 0) {
      return { maxValue: 100, ticks: [0, 25, 50, 75, 100] };
    }

    let max = 0;
    data.forEach((item) => {
      areas.forEach((area) => {
        const value = item[area.dataKey];
        if (typeof value === "number" && value > max) {
          max = value;
        }
      });
    });

    // Round up to nearest multiple of 10
    const roundedMax = Math.ceil(max / 10) * 10 + 20;
    const tickInterval = Math.ceil(roundedMax / 4 / 10) * 10;
    const calculatedTicks = Array.from(
      { length: 5 },
      (_, i) => i * tickInterval
    );

    return { maxValue: roundedMax, ticks: calculatedTicks };
  }, [data, areas]);

  // Calculate optimal tick interval to show ~10-12 labels
  const xAxisInterval = useMemo(() => {
    if (!data || data.length === 0) return 0;
    const dataLength = data.length;

    // Show all labels if data points are <= 12
    if (dataLength <= 12) return 0;

    // Calculate interval to show approximately 10-12 labels
    return Math.ceil(dataLength / 12) - 1;
  }, [data]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box className={styles.customTooltip}>
          <Typography className={styles.tooltipLabel}>{label}</Typography>
          {payload.map((entry: any, index: number) => (
            <Typography
              key={`item-${index}`}
              className={styles.tooltipItem}
              style={{ color: entry.stroke }}
            >
              {entry.name}: {entry.value}
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  const CustomLegend = () => {
    return (
      <Box className={styles.customLegend}>
        {areas.map((area, index) => (
          <Box key={index} className={styles.legendItem}>
            <Box
              className={styles.legendDot}
              style={{ backgroundColor: area.color }}
            />
            <Typography className={styles.legendText}>{area.label}</Typography>
          </Box>
        ))}
      </Box>
    );
  };

  if (!data || data.length === 0) {
    return (
      <Box className={styles.container}>
        <Box className={styles.header}>
          <Typography variant="h6" className={styles.title}>
            {title}
          </Typography>
          {showThisYear && (
            <Chip
              label="This Year"
              size="small"
              sx={{
                backgroundColor: "#e6faf5",
                color: "#00d2aa",
                fontWeight: 600,
                fontSize: "0.75rem",
              }}
            />
          )}
        </Box>
        <Box
          className={styles.chartContainer}
          display="flex"
          alignItems="center"
          justifyContent="center"
          minHeight={height}
        >
          <Typography variant="body2" color="textSecondary">
            No data available
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Typography variant="h6" className={styles.title}>
          {title}
        </Typography>
        {showThisYear && (
          <Chip
            label="This Year"
            size="small"
            sx={{
              backgroundColor: "#e6faf5",
              color: "#00d2aa",
              fontWeight: 500,
              fontSize: "0.75rem",
            }}
          />
        )}
      </Box>

      <Box className={styles.chartContainer}>
        <ResponsiveContainer width="100%" height={height}>
          <AreaChart
            data={data}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <defs>
              {areas.map((area, index) => (
                <linearGradient
                  key={index}
                  id={`color${area.dataKey}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="5%" stopColor={area.color} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={area.color} stopOpacity={0.1} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey={xAxisKey}
              tick={{ fill: "#666666", fontSize: 12 }}
              axisLine={{ stroke: "#e0e0e0" }}
              interval={xAxisInterval}
            />
            <YAxis
              tick={{ fill: "#666666", fontSize: 12 }}
              axisLine={{ stroke: "#e0e0e0" }}
              domain={[0, maxValue]}
              ticks={ticks}
            />
            <Tooltip content={<CustomTooltip />} />
            {areas.map((area, index) => (
              <Area
                key={index}
                type="monotone"
                dataKey={area.dataKey}
                stroke={area.color}
                strokeWidth={2}
                fillOpacity={1}
                fill={`url(#color${area.dataKey})`}
                name={area.label}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </Box>

      <CustomLegend />
    </Box>
  );
};

export default CustomAreaChart;
